// Byte and text codecs shared by the Next Steps encryption modules. Pure JS on
// purpose: the API validates every wrapping field as standard base64 with
// padding and the two hashes as base64url without, and neither a WebView nor
// Jest's environments are guaranteed the same atob/btoa behaviour for the
// edge cases (padding, the url alphabet), so the codec is spelled out here.

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BASE64URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const BASE64_PATTERN = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
const BASE64URL_PATTERN = /^[A-Za-z0-9_-]*$/;

/**
 * @param {string} alphabet
 * @returns {Record<string, number>}
 */
function buildLookup(alphabet) {
  return Object.fromEntries(Array.from(alphabet, (char, index) => [char, index]));
}

const BASE64_LOOKUP = buildLookup(BASE64_ALPHABET);
const BASE64URL_LOOKUP = buildLookup(BASE64URL_ALPHABET);

/**
 * @param {Uint8Array} bytes
 * @param {string} alphabet
 * @returns {string} unpadded encoding
 */
function encodeBase64Core(bytes, alphabet) {
  const chars = [];
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const c = i + 2 < bytes.length ? bytes[i + 2] : 0;
    const triple = (a << 16) | (b << 8) | c;
    chars.push(alphabet[(triple >> 18) & 63], alphabet[(triple >> 12) & 63]);
    if (i + 1 < bytes.length) chars.push(alphabet[(triple >> 6) & 63]);
    if (i + 2 < bytes.length) chars.push(alphabet[triple & 63]);
  }
  return chars.join('');
}

/**
 * @param {string} text unpadded, already validated against the alphabet
 * @param {Record<string, number>} lookup
 * @returns {Uint8Array}
 */
function decodeBase64Core(text, lookup) {
  const fullBytes = Math.floor((text.length * 6) / 8);
  const bytes = new Uint8Array(fullBytes);
  let buffer = 0;
  let bits = 0;
  let out = 0;
  for (const char of text) {
    buffer = (buffer << 6) | lookup[char];
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[out] = (buffer >> bits) & 255;
      out += 1;
    }
  }
  return bytes;
}

/**
 * @param {Uint8Array} bytes
 * @returns {string} standard base64 with padding, as the API's `@IsBase64` expects
 */
export function bytesToBase64(bytes) {
  const encoded = encodeBase64Core(bytes, BASE64_ALPHABET);
  const remainder = encoded.length % 4;
  return remainder === 0 ? encoded : encoded + '='.repeat(4 - remainder);
}

/**
 * @param {string} text
 * @returns {Uint8Array}
 */
export function base64ToBytes(text) {
  if (typeof text !== 'string' || !BASE64_PATTERN.test(text)) {
    throw new TypeError('Expected standard base64 text');
  }
  return decodeBase64Core(text.replace(/=+$/, ''), BASE64_LOOKUP);
}

/**
 * @param {Uint8Array} bytes
 * @returns {string} base64url without padding, as the API's hashes are shaped
 */
export function bytesToBase64Url(bytes) {
  return encodeBase64Core(bytes, BASE64URL_ALPHABET);
}

/**
 * @param {string} text
 * @returns {Uint8Array}
 */
export function base64UrlToBytes(text) {
  if (typeof text !== 'string' || !BASE64URL_PATTERN.test(text) || text.length % 4 === 1) {
    throw new TypeError('Expected base64url text without padding');
  }
  return decodeBase64Core(text, BASE64URL_LOOKUP);
}

/**
 * @param {string} text
 * @returns {Uint8Array}
 */
export function utf8Encode(text) {
  return new TextEncoder().encode(text);
}

/**
 * @param {Uint8Array | ArrayBuffer} bytes
 * @returns {string}
 */
export function utf8Decode(bytes) {
  return new TextDecoder().decode(bytes);
}

/**
 * @param {Uint8Array} bytes
 * @returns {string} lower-case hex
 */
export function bytesToHex(bytes) {
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * @param {...Uint8Array} arrays
 * @returns {Uint8Array} a new array holding every input in order
 */
export function concatBytes(...arrays) {
  const total = arrays.reduce((sum, array) => sum + array.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const array of arrays) {
    out.set(array, offset);
    offset += array.length;
  }
  return out;
}

/**
 * Crockford base32: digits and letters without I, L, O and U — the four a
 * person reading one screen and typing into another confuses with 1, 1, 0 and
 * V. Here rather than beside either of its callers because both the recovery
 * code (`recovery-code.js`) and the out-of-band approval code
 * (`approval-secret.js`) are rendered in it, and two `export const`s of the
 * same name would collide in this directory's `index.js` star exports.
 */
export const CROCKFORD_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
